import { ObjectFitContainer } from 'components/common/ObjectFitContainer/ObjectFitContainer';
import Progress from 'components/common/Progress/Progress';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import React from 'react';
import { Link } from 'react-router-dom';
import i18n from 'i18n';
import styles from './campaignDashboard.module.scss';
import { hasFuncs, isPmax3Order } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import { camelCase, floor, upperFirst } from 'lodash';
import classnames from 'classnames/bind';
import { numberWithCommas } from 'utils/StringUtil';
import { getCurrencyLabel } from 'helper/CurrencyHelper';
import { CampaignDashboardRecord } from 'core/campaignDashboard/CampaignDashboard';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { getSalesChannelName } from 'utils/I18nUtils';
import {
  AdjustmentTagType,
  adjustmentTagColorGroup
} from 'core/campaignDashboard/AdjustmentTagType';

const cx = classnames.bind(styles);

const getCampaignTypeDes = (campaign: CampaignDashboardRecord) => {
  if (campaign.isPmp && campaign.channelGroupId !== undefined) {
    const channelGroupSetting = ChannelGroupSetting.fromId(
      campaign.channelGroupId
    );
    return `${getSalesChannelName(
      channelGroupSetting.config.id,
      channelGroupSetting.config.name
    )} PMP`;
  }
  const type = campaign.adType;
  const key = `campaignList.labels.adType${upperFirst(camelCase(type))}`;
  if (type === '' || type === undefined) {
    return i18n.t('campaignList.labels.adTypeEmpty');
  }
  if (!i18n.exists(key)) {
    return type;
  }

  return i18n.t(key);
};

const nameFormatter = (
  id: string | number,
  campaign: CampaignDashboardRecord
): any => {
  const renderLabel = () => (
    <span className={styles.campaignName}>{campaign.campaignName}</span>
  );
  const editPath = `/orders/${campaign.orderNumber}/campaign-groups/${campaign.l1ObjectId}/campaigns/${id}/edit`;
  return (
    <div className={styles.info}>
      <PermissionChecker
        permissionAware={hasFuncs(Permission.CAMPAIGN_WRITE).and(
          isPmax3Order(campaign.orderId)
        )}
        renderOtherwise={renderLabel}
      >
        <Link to={editPath} className={`${styles.campaignName} ${styles.link}`}>
          {campaign.campaignName}
        </Link>
      </PermissionChecker>
      <div className={styles.campaignID}>
        <div>{`ID: ${id}`}</div>
        <div>
          <ObjectFitContainer
            key={i18n.language}
            containerHeight={16}
            onlyScaleDown={true}
            alignItems='auto'
            justifyContent='auto'
            margin={0}
          >
            {getCampaignTypeDes(campaign)}
          </ObjectFitContainer>
        </div>
      </div>
    </div>
  );
};

// ECOPD-5476: adjustment-tag capsules for the dedicated list column. Reuses the
// shared `.capsules` > `.capsule` convention (see SiteAnnouncementTypeCapsule /
// L1ObjectList); colour group resolves to $deep-warning / $warning / $danger.
// Unknown/future codes fall back to a danger capsule with the raw code.
const adjustmentTagsFormatter = (adjustmentTags: string[] | undefined): any => {
  if (!adjustmentTags || adjustmentTags.length === 0) {
    return null;
  }
  return (
    <div className={styles.capsules}>
      {adjustmentTags.map((tag) => {
        const colorGroup =
          adjustmentTagColorGroup[tag as AdjustmentTagType] ?? 'danger';
        // Wire code stays UPPER_SNAKE; i18n key is its camelCase form.
        const i18nKey = `adjustmentTags.${camelCase(tag)}`;
        const label = i18n.exists(i18nKey) ? i18n.t(i18nKey) : tag;
        return (
          <div key={tag} className={cx('capsule', colorGroup)}>
            {label}
          </div>
        );
      })}
    </div>
  );
};

const progressFormatter = (
  executeRate,
  campaign: CampaignDashboardRecord & {
    predictRate: number;
    spents: number;
  }
): any => {
  const predictRate = Math.min(campaign.predictRate, 1);
  const warnignLine = campaign.budget * 1.1;
  const discrepancy = predictRate - executeRate;

  const currencyLabel = getCurrencyLabel(campaign.currency);
  const spent = `${currencyLabel} ${numberWithCommas(floor(campaign.spent, 2).toFixed(2))}`;
  const budget = `${currencyLabel} ${numberWithCommas(floor(campaign.budget, 2).toFixed(2))}`;
  const danger = campaign.spents > warnignLine;
  const deepWarning = discrepancy > 0.03;
  const warning = discrepancy > 0.01 && discrepancy <= 0.03;

  return (
    <div className={styles.progressContent}>
      <div className={styles.progress}>
        <Progress
          progress={executeRate}
          danger={danger}
          deepWarning={deepWarning}
          warning={warning}
        />
      </div>
      {`${spent} / ${budget}`}
      <br />
      {`${(executeRate * 100).toFixed(2)}% / ${(predictRate * 100).toFixed(2)}%`}
    </div>
  );
};

const formatters = {
  nameFormatter,
  progressFormatter,
  adjustmentTagsFormatter
};

export default formatters;
